import {
  BackgroundVariant,
  type ReactFlowProps as XYFlowProps,
  type Node,
  type Edge,
  type FitViewOptions,
  type ControlProps as ControlsProps,
  type MiniMapProps,
  type BackgroundProps,
  type Viewport,
  type ReactFlowInstance,
} from '@xyflow/react';

// Background configuration - Pick from Background component props
export type BackgroundConfig = Pick<
  BackgroundProps,
  'variant' | 'gap' | 'size' | 'color' | 'className' | 'style'
>;

// MiniMap configuration - Pick from MiniMap component props + show toggle
export type MiniMapConfig = Pick<
  MiniMapProps,
  | 'nodeColor'
  | 'nodeStrokeColor'
  | 'nodeStrokeWidth'
  | 'nodeBorderRadius'
  | 'maskColor'
  | 'position'
  | 'className'
  | 'style'
  | 'zoomable'
  | 'pannable'
> & {
  /** Whether to show the minimap */
  show?: boolean;
};

// Controls configuration - Pick from Controls component props + show toggle
export type ControlsConfig = Omit<ControlsProps, 'show'> & {
  /** Whether to show the controls */
  show?: boolean;
};

// Canvas/Interaction configuration - Pick from ReactFlow component props
export type CanvasConfig = Pick<
  XYFlowProps,
  | 'nodesDraggable'
  | 'nodesConnectable'
  | 'elementsSelectable'
  | 'zoomOnScroll'
  | 'zoomOnPinch'
  | 'zoomOnDoubleClick'
  | 'panOnDrag'
  | 'panOnScroll'
  | 'panOnScrollMode'
  | 'preventScrolling'
  | 'minZoom'
  | 'maxZoom'
  | 'defaultViewport'
  | 'fitView'
  | 'fitViewOptions'
  | 'connectionMode'
  | 'snapToGrid'
  | 'snapGrid'
>;

// Main ReactFlow component props - Pick from ReactFlow + custom config props
export type ReactFlowProps = Pick<
  XYFlowProps,
  // Required controlled state
  | 'nodes'
  | 'edges'
  // Change handlers
  | 'onNodesChange'
  | 'onEdgesChange'
  // Connection handlers
  | 'onConnect'
  | 'onConnectStart'
  | 'onConnectEnd'
  // Custom node/edge types
  | 'nodeTypes'
  | 'edgeTypes'
  // Default options
  | 'defaultEdgeOptions'
  // Node event handlers
  | 'onNodeClick'
  | 'onNodeDoubleClick'
  | 'onNodeContextMenu'
  | 'onNodeDrag'
  | 'onNodeDragStart'
  | 'onNodeDragStop'
  // Edge event handlers
  | 'onEdgeClick'
  | 'onEdgeDoubleClick'
  | 'onEdgeContextMenu'
  // Pane handlers
  | 'onPaneClick'
  | 'onPaneContextMenu'
  // Selection handlers
  | 'onSelectionChange'
  // Delete handlers
  | 'onNodesDelete'
  | 'onEdgesDelete'
  // Styling
  | 'className'
  | 'style'
> & {
  // Configuration objects
  backgroundConfig?: BackgroundConfig;
  miniMapConfig?: MiniMapConfig;
  controlsConfig?: ControlsConfig;
  canvasConfig?: CanvasConfig;

  /** When true, disables all editing (dragging, connecting, selecting) */
  readOnly?: boolean;

  /** Show background */
  showBackground?: boolean;

  children?: React.ReactNode;
};

// Default configurations
export const defaultBackgroundConfig: BackgroundConfig = {
  variant: BackgroundVariant.Dots,
  gap: 16,
  size: 1,
};

export const defaultMiniMapConfig: MiniMapConfig = {
  show: false,
  position: 'bottom-right',
  zoomable: true,
  pannable: true,
};

export const defaultControlsConfig: ControlsConfig = {
  show: true,
  showZoom: true,
  showFitView: true,
  showInteractive: true,
  position: 'bottom-left',
};

export const defaultCanvasConfig: CanvasConfig = {
  nodesDraggable: true,
  nodesConnectable: true,
  elementsSelectable: true,
  zoomOnScroll: true,
  zoomOnPinch: true,
  zoomOnDoubleClick: true,
  panOnDrag: true,
  panOnScroll: false,
  preventScrolling: true,
  minZoom: 0.1,
  maxZoom: 4,
  fitView: true,
  snapToGrid: false,
};

// Keys that should be disabled when canvas is not editable
export const editableCanvasKeys: (keyof CanvasConfig)[] = [
  'nodesDraggable',
  'nodesConnectable',
  'elementsSelectable',
];

// Ref handle type for imperative methods
export type ReactFlowHandle = { instance: ReactFlowInstance<Node, Edge> };

// Re-export useful types from @xyflow/react for convenience
export type { Node, Edge, FitViewOptions, Viewport, ReactFlowInstance };
